import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { ZONE_COLORS, getNextAvailableZoneColor } from '../../utils/warehouseUtils';
import type { WarehouseZone, WarehouseRack, ZoneCreateData, ZoneUpdateData, MeasurementUnit } from '../../types/warehouse';
import {
  Plus,
  Layers,
  Grid3X3,
  Edit2,
  Trash2,
  X,
  Check,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';

interface WarehouseZonesTabProps {
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  unit: MeasurementUnit;
  onCreateZone: (data: Omit<ZoneCreateData, 'warehouse_id'>) => Promise<WarehouseZone | null>;
  onUpdateZone: (id: string, data: ZoneUpdateData) => Promise<WarehouseZone | null>;
  onDeleteZone: (id: string) => Promise<boolean>;
}

export const WarehouseZonesTab: React.FC<WarehouseZonesTabProps> = ({
  zones,
  racks,
  unit,
  onCreateZone,
  onUpdateZone,
  onDeleteZone,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingZone, setEditingZone] = useState<WarehouseZone | null>(null);
  const [deletingZone, setDeletingZone] = useState<WarehouseZone | null>(null);

  const getRacksInZone = (zoneId: string) => racks.filter((r) => r.zone_id === zoneId);

  const handleCreateZone = async (data: Omit<ZoneCreateData, 'warehouse_id'>) => {
    const zone = await onCreateZone(data);
    if (zone) {
      setShowCreateModal(false);
    }
    return zone;
  };

  const handleUpdateZone = async (id: string, data: ZoneUpdateData) => {
    const zone = await onUpdateZone(id, data);
    if (zone) {
      setEditingZone(null);
    }
    return zone;
  };

  const handleDeleteZone = async (id: string) => {
    const success = await onDeleteZone(id);
    if (success) {
      setDeletingZone(null);
    }
    return success;
  };

  if (zones.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Layers className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          No Zones Created
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          Zones help you organize your warehouse into logical areas. Create zones to group racks and
          make navigation easier.
        </p>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create First Zone
        </Button>

        {showCreateModal && (
          <CreateZoneModal
            existingZones={zones}
            unit={unit}
            onSubmit={handleCreateZone}
            onClose={() => setShowCreateModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Zones ({zones.length})
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Organize your warehouse into logical areas
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Zone
        </Button>
      </div>

      <div className="grid gap-4">
        {zones.map((zone) => {
          const zoneRacks = getRacksInZone(zone.id);
          return (
            <ZoneCard
              key={zone.id}
              zone={zone}
              rackCount={zoneRacks.length}
              onEdit={() => setEditingZone(zone)}
              onDelete={() => setDeletingZone(zone)}
            />
          );
        })}
      </div>

      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <Grid3X3 className="h-4 w-4" />
          <span>
            <strong className="text-gray-900 dark:text-gray-100">
              {racks.filter((r) => !r.zone_id).length}
            </strong>{' '}
            racks are unassigned to any zone
          </span>
        </div>
      </div>

      {showCreateModal && (
        <CreateZoneModal
          existingZones={zones}
          unit={unit}
          onSubmit={handleCreateZone}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {editingZone && (
        <EditZoneModal
          zone={editingZone}
          existingZones={zones}
          unit={unit}
          onSubmit={(data) => handleUpdateZone(editingZone.id, data)}
          onClose={() => setEditingZone(null)}
        />
      )}

      {deletingZone && (
        <DeleteZoneModal
          zone={deletingZone}
          rackCount={getRacksInZone(deletingZone.id).length}
          onConfirm={() => handleDeleteZone(deletingZone.id)}
          onCancel={() => setDeletingZone(null)}
        />
      )}
    </div>
  );
};

interface ZoneCardProps {
  zone: WarehouseZone;
  rackCount: number;
  onEdit: () => void;
  onDelete: () => void;
}

const ZoneCard: React.FC<ZoneCardProps> = ({ zone, rackCount, onEdit, onDelete }) => {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
            style={{ backgroundColor: zone.color }}
          >
            {zone.zone_code}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Zone {zone.zone_code}
              </h4>
              {zone.zone_name && (
                <>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">{zone.zone_name}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Grid3X3 className="h-3.5 w-3.5" />
                {rackCount} rack{rackCount !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: zone.color }}
                />
                {zone.color.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title="Edit zone"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Delete zone"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

interface CreateZoneModalProps {
  existingZones: WarehouseZone[];
  unit: MeasurementUnit;
  onSubmit: (data: Omit<ZoneCreateData, 'warehouse_id'>) => Promise<WarehouseZone | null>;
  onClose: () => void;
}

const CreateZoneModal: React.FC<CreateZoneModalProps> = ({ existingZones, unit, onSubmit, onClose }) => {
  const [zoneName, setZoneName] = useState('');
  const [selectedColor, setSelectedColor] = useState(getNextAvailableZoneColor(existingZones));
  const [zoneLength, setZoneLength] = useState<number>(unit === 'cm' ? 1000 : 10);
  const [zoneWidth, setZoneWidth] = useState<number>(unit === 'cm' ? 1000 : 10);
  const [saving, setSaving] = useState(false);

  const unitLabel = unit === 'cm' ? 'cm' : 'm';
  const minValue = unit === 'cm' ? 100 : 1;
  const maxValue = unit === 'cm' ? 10000 : 100;
  const stepValue = unit === 'cm' ? 100 : 1;

  const usedColors = new Set(existingZones.map((z) => z.color));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit({
      zone_name: zoneName.trim() || undefined,
      color: selectedColor,
      zone_width: zoneLength,
      zone_height: zoneWidth,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Create Zone</h2>
                <p className="text-blue-100 text-sm">Add a new zone to your warehouse</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zone Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              placeholder="e.g., Receiving, Shipping, Cold Storage"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              A descriptive name helps identify the zone's purpose
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Length ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoneLength(Math.max(minValue, zoneLength - stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minValue}
                  max={maxValue}
                  step={stepValue}
                  value={zoneLength}
                  onChange={(e) => setZoneLength(Math.max(minValue, Math.min(maxValue, parseFloat(e.target.value) || minValue)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setZoneLength(Math.min(maxValue, zoneLength + stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Width ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoneWidth(Math.max(minValue, zoneWidth - stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minValue}
                  max={maxValue}
                  step={stepValue}
                  value={zoneWidth}
                  onChange={(e) => setZoneWidth(Math.max(minValue, Math.min(maxValue, parseFloat(e.target.value) || minValue)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setZoneWidth(Math.min(maxValue, zoneWidth + stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Zone Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {ZONE_COLORS.map((color) => {
                const isUsed = usedColors.has(color);
                const isSelected = selectedColor === color;
                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => !isUsed && setSelectedColor(color)}
                    disabled={isUsed}
                    className={`relative w-10 h-10 rounded-lg transition-all ${
                      isUsed
                        ? 'opacity-30 cursor-not-allowed'
                        : isSelected
                        ? 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={isUsed ? 'Already in use' : color}
                  >
                    {isSelected && (
                      <Check className="h-5 w-5 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: selectedColor }}
              >
                {String.fromCharCode(65 + existingZones.length)}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  Zone {String.fromCharCode(65 + existingZones.length)}
                  {zoneName && ` - ${zoneName}`}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Preview</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} className="flex-1">
              Create Zone
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditZoneModalProps {
  zone: WarehouseZone;
  existingZones: WarehouseZone[];
  unit: MeasurementUnit;
  onSubmit: (data: ZoneUpdateData) => Promise<WarehouseZone | null>;
  onClose: () => void;
}

const EditZoneModal: React.FC<EditZoneModalProps> = ({
  zone,
  existingZones,
  unit,
  onSubmit,
  onClose,
}) => {
  const [zoneName, setZoneName] = useState(zone.zone_name || '');
  const [selectedColor, setSelectedColor] = useState(zone.color);
  const [zoneLength, setZoneLength] = useState<number>(zone.zone_width || (unit === 'cm' ? 1000 : 10));
  const [zoneWidth, setZoneWidth] = useState<number>(zone.zone_height || (unit === 'cm' ? 1000 : 10));
  const [saving, setSaving] = useState(false);

  const unitLabel = unit === 'cm' ? 'cm' : 'm';
  const minValue = unit === 'cm' ? 100 : 1;
  const maxValue = unit === 'cm' ? 10000 : 100;
  const stepValue = unit === 'cm' ? 100 : 1;

  const usedColors = new Set(existingZones.filter((z) => z.id !== zone.id).map((z) => z.color));

  const hasChanges = zoneName !== (zone.zone_name || '') || selectedColor !== zone.color || zoneLength !== zone.zone_width || zoneWidth !== zone.zone_height;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    await onSubmit({
      zone_name: zoneName.trim() || '',
      color: selectedColor,
      zone_width: zoneLength,
      zone_height: zoneWidth,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: zone.color }}
              >
                {zone.zone_code}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Edit Zone {zone.zone_code}</h2>
                <p className="text-blue-100 text-sm">Update zone details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zone Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              placeholder="e.g., Receiving, Shipping, Cold Storage"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Length ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoneLength(Math.max(minValue, zoneLength - stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minValue}
                  max={maxValue}
                  step={stepValue}
                  value={zoneLength}
                  onChange={(e) => setZoneLength(Math.max(minValue, Math.min(maxValue, parseFloat(e.target.value) || minValue)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setZoneLength(Math.min(maxValue, zoneLength + stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Width ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoneWidth(Math.max(minValue, zoneWidth - stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minValue}
                  max={maxValue}
                  step={stepValue}
                  value={zoneWidth}
                  onChange={(e) => setZoneWidth(Math.max(minValue, Math.min(maxValue, parseFloat(e.target.value) || minValue)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setZoneWidth(Math.min(maxValue, zoneWidth + stepValue))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Zone Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {ZONE_COLORS.map((color) => {
                const isUsed = usedColors.has(color);
                const isSelected = selectedColor === color;
                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => !isUsed && setSelectedColor(color)}
                    disabled={isUsed}
                    className={`relative w-10 h-10 rounded-lg transition-all ${
                      isUsed
                        ? 'opacity-30 cursor-not-allowed'
                        : isSelected
                        ? 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={isUsed ? 'Already in use' : color}
                  >
                    {isSelected && (
                      <Check className="h-5 w-5 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              disabled={!hasChanges}
              className="flex-1"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteZoneModalProps {
  zone: WarehouseZone;
  rackCount: number;
  onConfirm: () => Promise<boolean>;
  onCancel: () => void;
}

const DeleteZoneModal: React.FC<DeleteZoneModalProps> = ({
  zone,
  rackCount,
  onConfirm,
  onCancel,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Delete Zone</h2>
                <p className="text-red-100 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: zone.color }}
            >
              {zone.zone_code}
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Zone {zone.zone_code}
                {zone.zone_name && ` - ${zone.zone_name}`}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {rackCount} rack{rackCount !== 1 ? 's' : ''} assigned
              </div>
            </div>
          </div>

          {rackCount > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Note:</strong> The {rackCount} rack{rackCount !== 1 ? 's' : ''} in this zone
                will become unassigned but will not be deleted.
              </p>
            </div>
          )}

          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete this zone? This action cannot be undone.
          </p>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirm}
              loading={deleting}
              className="flex-1"
            >
              Delete Zone
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
